import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Card, Switch, Typography, Button, Divider, message, Modal } from 'antd';
import { ArrowLeftOutlined, CheckOutlined, GoogleOutlined } from '@ant-design/icons';
import AppContext from '../context/AppContext';
import packageJson from '../../package.json';
import { signInWithGoogle, signOutOfAccount } from '../services/authService';
const { Title, Text } = Typography;

const themeColors = [
  { name: 'Blue', value: '#1890ff' },
  { name: 'Purple', value: '#722ed1' },
  { name: 'Cyan', value: '#13c2c2' },
  { name: 'Green', value: '#52c41a' },
  { name: 'Magenta', value: '#eb2f96' },
  { name: 'Orange', value: '#fa8c16' },
  { name: 'Volcano', value: '#fa541c' },
  { name: 'Yellow', value: '#fadb14' },
  { name: 'Lime', value: '#a0d911' },
];

// Styles for the scrollable container
const scrollableContainerStyle = {
  display: 'flex',
  gap: '12px',
  overflowX: 'auto',
  paddingBottom: '8px',
  paddingTop: '4px',
  WebkitOverflowScrolling: 'touch',
  // Hide scrollbar for Firefox
  scrollbarWidth: 'none',
  // Hide scrollbar for IE/Edge
  msOverflowStyle: 'none'
};

// Add a style tag to hide webkit scrollbar
const styleTag = document.createElement('style');
styleTag.textContent = `
  .color-picker-scroll::-webkit-scrollbar {
    display: none;
  }
`;
document.head.appendChild(styleTag);

const Settings = ({ onClose }) => {
  const { state, dispatch } = useContext(AppContext);

  const handleShoppingModeChange = () => {
    dispatch({ type: 'TOGGLE_SHOPPING_MODE' });
  };

  const handleThemeChange = () => {
    dispatch({ type: 'TOGGLE_THEME' });
  };

  const handleThemeColorChange = (color) => {
    dispatch({ type: 'SET_THEME_COLOR', payload: color });
  };

  const handleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      message.error('Failed to sign in: ' + error.message);
    }
  };

  const handleSignOut = async () => {
    Modal.confirm({
      title: 'Sign Out',
      content: 'Are you sure you want to sign out? Your data will be removed from this device.',
      okText: 'Sign Out',
      okButtonProps: {
        danger: true
      },
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await signOutOfAccount();
        } catch (error) {
          message.error('Failed to sign out: ' + error.message);
        }
      }
    });
  };

  return (
    <div style={{ 
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        marginBottom: '24px',
        gap: '16px'
      }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          type="text"
          onClick={onClose}
        />
        <Title level={4} style={{ margin: 0 }}>Settings</Title>
      </div>
      <Card style={{ flex: 1 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center' 
          }}>
            <Title level={5} style={{ margin: 0 }}>Shopping List Mode</Title>
            <Switch 
              checked={state.shoppingMode} 
              onChange={handleShoppingModeChange}
            />
          </div>
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center' 
          }}>
            <Title level={5} style={{ margin: 0 }}>Dark Theme</Title>
            <Switch 
              checked={state.theme === 'dark'}
              onChange={handleThemeChange}
            />
          </div>

          <div>
            <Title level={5} style={{ marginTop: 0, marginBottom: '12px' }}>Theme Color</Title>
            <div className="color-picker-scroll" style={scrollableContainerStyle}>
              {themeColors.map((color) => (
                <div
                  key={color.value}
                  onClick={() => handleThemeColorChange(color.value)}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: color.value,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    border: state.themeColor === color.value ? '2px solid #fff' : 'none',
                    boxShadow: state.themeColor === color.value 
                      ? '0 0 0 2px ' + color.value 
                      : 'none'
                  }}
                >
                  {state.themeColor === color.value && (
                    <CheckOutlined style={{ color: '#fff', fontSize: '20px' }} />
                  )}
                </div>
              ))}
            </div>
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            {state.user ? (
              <Button 
                type="primary" 
                onClick={handleSignOut}
                danger
                style={{ minWidth: '220px' }}
              >
                Sign Out
              </Button>
            ) : (
              <Button 
                type="default"
                onClick={handleSignIn}
                icon={<GoogleOutlined />}
                style={{
                  minWidth: '220px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  backgroundColor: '#fff',
                  border: '1px solid #ddd',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  height: '40px'
                }}
              >
                Sign in with Google
              </Button>
            )}
          </div>
        </div>
      </Card>
      <div style={{ 
        color: state.theme === 'light' ? 'rgba(0, 0, 0, 0.45)' : 'rgba(255, 255, 255, 0.45)',
        display: 'flex',
        gap: '8px',
        position: 'fixed',
        bottom: '2px',
        width: '100%',
        justifyContent: 'center',
        left: 0
      }}>
        <Text type="secondary">Version {packageJson.version}</Text>
        <Divider type="vertical" />
        <Text type="secondary">
          Made with <span style={{ color: '#ff4d4f' }}>❤</span> by Vishnu053
        </Text>
      </div>
    </div>
  );
};

Settings.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default Settings; 